import { useEffect } from "react";
import { createPortal } from "react-dom";
import { Loader2, X } from "lucide-react";
import type { KbGenerationTemplateDetail } from "../../types/gameRuleKbGenerationTemplate";
import { formatIsoDateOnly } from "../../utils/displayDate";

export type KbGenerationTemplateDetailSheetProps = {
  readonly open: boolean;
  readonly template: KbGenerationTemplateDetail | null;
  readonly sectionNames: readonly string[];
  readonly loading: boolean;
  readonly error: string | null;
  readonly onClose: () => void;
  readonly onUseTemplate: () => void;
};

const formatTemplateMeta = (
  template: KbGenerationTemplateDetail,
): string => {
  const parts = [
    `${template.sectionCount} section${template.sectionCount === 1 ? "" : "s"}`,
  ];
  if (template.totalVersions > 1) {
    parts.push(`v${template.version}`);
  }
  parts.push(`Updated ${formatIsoDateOnly(template.updatedAt)}`);
  return parts.join(" · ");
};

export const KbGenerationTemplateDetailSheet = ({
  open,
  template,
  sectionNames,
  loading,
  error,
  onClose,
  onUseTemplate,
}: KbGenerationTemplateDetailSheetProps) => {
  useEffect(() => {
    if (!open) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex flex-col bg-white"
      role="dialog"
      aria-modal="true"
      aria-labelledby="kb-generation-template-detail-title"
    >
      <div className="flex shrink-0 items-start justify-between gap-3 border-b border-[var(--border)] bg-white px-4 py-3 shadow-sm">
        <div className="min-w-0">
          <h2
            id="kb-generation-template-detail-title"
            className="text-sm font-bold text-foreground"
          >
            Template details
          </h2>
          {template ? (
            <>
              <p className="mt-1 truncate text-xs font-semibold text-primary">
                {template.name}
              </p>
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                {formatTemplateMeta(template)}
              </p>
            </>
          ) : null}
        </div>
        <button
          type="button"
          className="shrink-0 rounded p-1 text-muted-foreground hover:bg-muted"
          aria-label="Close"
          onClick={onClose}
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto bg-white px-4 py-3">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
            Loading template…
          </div>
        ) : error ? (
          <p className="rounded border border-destructive/40 bg-destructive/10 px-3 py-3 text-xs text-destructive">
            {error}
          </p>
        ) : template ? (
          <div className="space-y-4">
            {template.totalVersions > 1 ? (
              <p className="text-[11px] text-muted-foreground">
                Version history: {template.totalVersions} version
                {template.totalVersions === 1 ? "" : "s"} (showing active v
                {template.version})
              </p>
            ) : null}
            <div>
              <p className="mb-2 text-xs font-bold text-foreground">
                Sections (in order)
              </p>
              {sectionNames.length === 0 ? (
                <p className="text-[11px] text-muted-foreground">
                  No section details available.
                </p>
              ) : (
                <ol className="space-y-1.5 rounded-md border border-[var(--border)] bg-slate-50 px-3 py-3">
                  {sectionNames.map((name, index) => (
                    <li
                      key={`${index}-${name}`}
                      className="flex gap-2 text-[11px] leading-snug text-foreground"
                    >
                      <span className="w-4 shrink-0 font-bold text-violet-700">
                        {index + 1}.
                      </span>
                      <span className="min-w-0">{name}</span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        ) : null}
      </div>

      <div className="shrink-0 space-y-2 border-t border-[var(--border)] bg-white px-4 py-3">
        <button
          type="button"
          className="w-full rounded bg-violet-700 px-4 py-2.5 text-xs font-bold text-white disabled:opacity-50"
          disabled={loading || Boolean(error) || !template}
          onClick={onUseTemplate}
        >
          Use this template
        </button>
        <button
          type="button"
          className="w-full rounded border border-input bg-background px-4 py-2.5 text-xs font-semibold text-foreground"
          onClick={onClose}
        >
          Cancel
        </button>
      </div>
    </div>,
    document.body,
  );
};
